// Deterministic descriptive evidence, not a guarantee of independent payment
// attempts or a family-wide significance test across all dashboard segments.
const DAY = 86400000;
const time = day => Date.parse(day + 'T00:00:00Z');
const dayString = ms => new Date(ms).toISOString().slice(0, 10);
const aggregate = rows => rows.reduce((sum, row) => ({ success: sum.success + row.success, total: sum.total + row.total }), { success: 0, total: 0 });
const rounded = n => Math.round(n * 100) / 100;

// Two-sided 95% Wilson score interval. Formula: NIST e-Handbook prc241.
export function wilson(success, total) {
  if (!Number.isSafeInteger(total) || !Number.isSafeInteger(success) || total <= 0 || success < 0 || success > total) return null;
  const z = 1.959963984540054, z2 = z * z, rate = success / total;
  const denominator = 1 + z2 / total;
  const center = (rate + z2 / (2 * total)) / denominator;
  const half = z * Math.sqrt(rate * (1 - rate) / total + z2 / (4 * total * total)) / denominator;
  return { lower: success === 0 ? 0 : Math.max(0, center - half), upper: success === total ? 1 : Math.min(1, center + half) };
}

export function rateEvidence(current, previous = null) {
  const interval = wilson(current.success, current.total);
  if (!interval) return null;
  const old = previous?.total >= 30 && !previous.inconsistent ? previous : null;
  const oldInterval = old ? wilson(old.success, old.total) : null;
  const rate = current.success / current.total;
  const before = oldInterval ? old.success / old.total : null;
  const belowTarget = rate < 0.8;
  const deteriorated = before !== null && (before - rate) * 100 >= 5 - 1e-9;
  const targetSupported = current.total >= 30 && interval.upper < 0.8;
  // Non-overlap of two Wilson intervals is deliberately conservative.
  const trendSupported = !!oldInterval && current.total >= 30 && interval.upper < oldInterval.lower;
  const historicalBaseline = before !== null && before > 0.8;
  const baseline = historicalBaseline ? before : 0.8;
  const baselineLower = historicalBaseline ? oldInterval.lower : 0.8;
  const extraDeclines = Math.max(0, (baseline - rate) * current.total);
  const conservativeExtraDeclines = Math.max(0, (baselineLower - interval.upper) * current.total);
  const supported = belowTarget && targetSupported || deteriorated && trendSupported;
  return {
    kind: belowTarget && deteriorated ? 'below_target_and_deterioration' : belowTarget ? 'below_target' : deteriorated ? 'deterioration' : 'normal',
    attempts: current.total,
    currentRate: rounded(rate * 100), previousRate: before === null ? null : rounded(before * 100),
    interval95: { lower: rounded(interval.lower * 100), upper: rounded(interval.upper * 100) },
    targetSupported, trendSupported,
    baseline: historicalBaseline ? 'previous' : 'target', baselineRate: rounded(baseline * 100),
    extraDeclines: rounded(extraDeclines), conservativeExtraDeclines: rounded(conservativeExtraDeclines),
    // This means two observed periods, never a claim of uninterrupted downtime.
    repeatedBelowTarget: !!oldInterval && oldInterval.upper < 0.8 && targetSupported,
    rankingImpact: Math.max(conservativeExtraDeclines, extraDeclines * (supported ? 0.5 : 0.1)),
  };
}

export function segmentPriority(evidence) {
  // Stay below mandatory overall/recent alerts (priority >= 92).
  const impact = evidence.rankingImpact;
  return 60 + 28 * impact / (impact + 50) + (evidence.repeatedBelowTarget ? 1 : 0);
}

export function lastCompleteDay(period) {
  return time(period.to) - (period.partial ? DAY : 0);
}

export function consecutiveLowDays(input) {
  const rows = new Map(input.current.series.map(row => [row.date, row]));
  let days = 0;
  for (let ms = lastCompleteDay(input.period); ; ms -= DAY) {
    const row = rows.get(dayString(ms));
    if (!row || row.total < 30 || row.success / row.total >= 0.8) break;
    days++;
  }
  return days;
}

export function recentWeekdayVolume(input) {
  if (input.current.inconsistent) return null;
  const current = new Map(input.current.series.map(row => [row.date, row]));
  const previousRows = input.previous?.data.inconsistent ? [] : input.previous?.data.series || [];
  const history = new Map([...previousRows, ...input.current.series].map(row => [row.date, row]));
  const recent = [], baseline = [];
  for (let offset = 2; offset >= 0; offset--) {
    const ms = lastCompleteDay(input.period) - offset * DAY;
    const now = current.get(dayString(ms)), old = history.get(dayString(ms - 7 * DAY));
    if (!now || !old) return null;
    recent.push(now); baseline.push(old);
  }
  const a = aggregate(recent), b = aggregate(baseline);
  return a.total >= 30 && b.total >= 30 ? { mode: 'same_weekdays_last_week', current: a.total, expected: b.total, ratio: a.total / b.total } : null;
}

function completeRows(data, period) {
  const expected = (lastCompleteDay(period) - time(period.from)) / DAY + 1;
  if (expected <= 0 || data.series.length !== expected) return null;
  if (!data.series.every((row, i) => time(row.date) === time(period.from) + i * DAY)) return null;
  const sum = aggregate(data.series);
  if (sum.total > data.total || sum.success > data.success || sum.total - sum.success > data.total - data.success) return null;
  // Complete-period series must agree with the aggregate before using them.
  if (!period.partial) {
    if (sum.total !== data.total || sum.success !== data.success) return null;
  }
  return data.series;
}

function weekdayWeights(period) {
  const result = Array(7).fill(0);
  for (let ms = time(period.from); ms <= time(period.to); ms += DAY) result[new Date(ms).getUTCDay()]++;
  const days = result.reduce((a, b) => a + b, 0);
  return result.map(n => n / days);
}

export function periodWeekdayVolume(input) {
  const prev = input.previous;
  if (!prev || prev.data.inconsistent || input.current.inconsistent || prev.data.total < 30) return null;
  const a = completeRows(input.current, input.period), b = completeRows(prev.data, prev.period);
  if (a && b) {
    const weekdays = Array.from({ length: 7 }, () => []);
    for (const row of b) weekdays[new Date(time(row.date)).getUTCDay()].push(row.total);
    const averages = weekdays.map(rows => rows.length ? rows.reduce((x, y) => x + y, 0) / rows.length : null);
    if (a.every(row => weekdays[new Date(time(row.date)).getUTCDay()].length >= 2)) {
      const current = aggregate(a).total;
      const expected = a.reduce((sum, row) => sum + averages[new Date(time(row.date)).getUTCDay()], 0);
      if (current >= 30 && expected >= 30) return { mode: 'weekday_adjusted', current, expected, ratio: current / expected };
    }
  }
  // If daily history is missing, aggregate averages are only comparable when
  // weekday proportions are identical (e.g. complete weeks or the same weekday).
  if (input.period.partial || prev.period.partial) return null;
  const weights = weekdayWeights(input.period), oldWeights = weekdayWeights(prev.period);
  if (!weights.every((value, i) => Math.abs(value - oldWeights[i]) < 1e-10)) return null;
  const days = (time(input.period.to) - time(input.period.from)) / DAY + 1;
  const oldDays = (time(prev.period.to) - time(prev.period.from)) / DAY + 1;
  const expected = prev.data.total / oldDays * days;
  return input.current.total >= 30 && expected >= 30 ? { mode: 'equal_weekday_weights', current: input.current.total, expected, ratio: input.current.total / expected } : null;
}

export function bankTrafficMix(input) {
  const cur = input.current, prev = input.previous?.data;
  if (!prev || cur.inconsistent || prev.inconsistent || !(cur.total >= 30) || !(prev.total >= 30)) return { available: false, reason: 'insufficient_data' };
  const bankMap = data => {
    const result = new Map();
    for (const bank of data.segments.filter(row => row.kind === 'bank')) {
      const key = bank.entity?.value;
      if (!key || result.has(key)) return null;
      result.set(key, bank);
    }
    const sum = aggregate([...result.values()]);
    if (sum.total > data.total || sum.success > data.success || sum.total - sum.success > data.total - data.success) return null;
    return result;
  };
  const currentBanks = bankMap(cur), previousBanks = bankMap(prev);
  if (!currentBanks || !previousBanks) return { available: false, reason: 'inconsistent_bank_rows' };
  const pairs = [...currentBanks].filter(([key, row]) => row.total >= 30 && previousBanks.get(key)?.total >= 30).map(([key, row]) => [row, previousBanks.get(key)]);
  const current = aggregate(pairs.map(pair => pair[0])), previous = aggregate(pairs.map(pair => pair[1]));
  const currentCoverage = current.total / cur.total, previousCoverage = previous.total / prev.total;
  const coverage = { currentCoverage: rounded(currentCoverage * 100), previousCoverage: rounded(previousCoverage * 100) };
  if (pairs.length < 2 || currentCoverage < 0.8 || previousCoverage < 0.8) return { available: false, reason: 'partial_bank_coverage', ...coverage };
  const before = previous.success / previous.total, after = current.success / current.total;
  const expected = pairs.reduce((sum, [now, old]) => sum + now.total / current.total * old.success / old.total, 0);
  const mixDelta = expected - before, withinDelta = after - expected, delta = after - before;
  return {
    available: true, ...coverage,
    previousRate: rounded(before * 100), currentRate: rounded(after * 100), expectedRateAtCurrentMix: rounded(expected * 100),
    mixDeltaPp: rounded(mixDelta * 100), withinBankDeltaPp: rounded(withinDelta * 100),
    mainlyMix: delta <= -0.05 + 1e-9 && mixDelta <= -0.03 && withinDelta > -0.02 && mixDelta / delta >= 0.7,
  };
}
